import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";
import { AuthInfoProvider } from "./context/AuthInfoContext";

import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/layout/AppShell";
import Spinner from "./components/common/Spinner";
import SessionExpiredModal from "./components/common/SessionExpiredModal";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { ToastProvider } from "./context/ToastContext";

// Lazy-loaded pages
const OverviewPage = lazy(() => import("./features/overview/pages/OverviewPage"));
const WorklistPage = lazy(() => import("./features/worklist/pages/WorklistPage"));
const AssetsPage = lazy(() => import("./features/assets/pages/AssetsPage"));
const MarketDataBootstrapPage = lazy(() => import("./features/marketData/pages/MarketDataBootstrapPage"));
const MarketRatesPage = lazy(() => import("./features/marketRates/pages/MarketRatesPage"));
const FixedIncomePage = lazy(() => import("./features/fixedIncome/pages/FixedIncomePage"));
const ContractsPage = lazy(() => import("./features/contracts/pages/ContractsPage"));
const SaaPage = lazy(() => import("./features/saa/pages/SaaPage"));
const Login = lazy(() => import("./components/Login"));
const PortfolioUploadPage = lazy(() => import("./pages/PortfolioUpload"));
const CashflowForecastPage = lazy(() => import("./features/cashflowForecast/pages"));
const BankflowPage = lazy(() => import("./features/bankflow/pages/BankflowPage"));
const BankImportPage = lazy(() => import("./features/bankImport/pages/BankImportPage"));
const BankProcessingPage = lazy(() => import("./features/bankProcessing/pages/BankProcessingPage"));
const ExactOnlinePage = lazy(() => import("./features/exactOnline/pages/ExactOnlinePage"));
const JournalsPage = lazy(() => import("./features/exactOnline/pages/JournalsPage"));
const GeneralLedgerPage = lazy(() => import("./features/exactOnline/pages/GeneralLedgerPage"));
const ExactCallback = lazy(() => import("./pages/ExactCallback"));
const Forbidden = lazy(() => import("./pages/Forbidden"));
const Diagnostics = lazy(() => import("./pages/Diagnostics"));
const LookupsPage = lazy(() => import("./features/lookups/pages/LookupsPage"));
const LegalEntitiesPage = lazy(() => import("./features/legalEntities/pages/LegalEntitiesPage"));
const BankDocumentsPage = lazy(() => import("./features/bankDocuments/pages/BankDocumentsPage"));
const BankProfilesPage = lazy(() => import("./features/bankProfiles/pages/BankProfilesPage"));
const FeedbackPage = lazy(() => import("./features/feedback/pages/FeedbackPage"));

// Foutgrens per route, binnen de ToastProvider en de shell: een crash raakt alleen het
// inhoudsgebied. De sleutel is het pad, dus naar een andere pagina gaan herstelt hem.
function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function page(element) {
  return (
    <RouteErrorBoundary>
      <ProtectedRoute>{element}</ProtectedRoute>
    </RouteErrorBoundary>
  );
}

function AppInner() {
  const isAuthenticated = useIsAuthenticated();
  return (
    <Router>
      <Suspense
        fallback={
          <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
            <Spinner />
          </div>
        }
      >
        <Routes>
          {/* Public routes buiten de shell */}
          <Route path="/login" element={<Login />} />
          {/* OAuth Exact callback SPA route should be PUBLIC (no ProtectedRoute) */}
          <Route path="/exact-callback" element={<ExactCallback />} />
          <Route path="/forbidden" element={<Forbidden />} />

          {/* Beveiligde pagina's binnen de app-shell (sidebar + topbar + save-bar) */}
          <Route element={<AppShell />}>
            <Route path="/" element={page(<OverviewPage />)} />
            <Route path="/worklist" element={page(<WorklistPage />)} />
            <Route path="/market-rates" element={page(<MarketRatesPage />)} />
            {/* Asset Allocations is samengevoegd met Assets; oude bookmarks blijven werken. */}
            <Route path="/asset-allocations" element={<Navigate to="/assets" replace />} />
            <Route path="/assets" element={page(<AssetsPage />)} />
            <Route path="/fixed-income" element={page(<FixedIncomePage />)} />
            {/* Termijnrekeningen staan in Liquiditeit (detailpaneel); oude bookmarks blijven werken. */}
            <Route path="/term-deposits" element={<Navigate to="/cashflow-forecast?tab=vandaag&detail=termijn" replace />} />
            <Route path="/contracts" element={page(<ContractsPage />)} />
            <Route path="/saa" element={page(<SaaPage />)} />
            <Route path="/market-data-bootstrap" element={page(<MarketDataBootstrapPage />)} />
            <Route path="/portfolio-upload" element={page(<PortfolioUploadPage />)} />
            <Route path="/cashflow-forecast" element={page(<CashflowForecastPage />)} />
            {/* Oude upload-route blijft werken via redirect */}
            <Route path="/cashflow-forecast-upload" element={<Navigate to="/cashflow-forecast" replace />} />
            <Route path="/bankflow" element={page(<BankflowPage />)} />
            <Route path="/bank-import" element={page(<BankImportPage />)} />
            <Route path="/bank-processing" element={page(<BankProcessingPage />)} />
            <Route path="/bank-documents" element={page(<BankDocumentsPage />)} />
            <Route path="/bank-profiles" element={page(<BankProfilesPage />)} />
            {/* Bestandsbeheer is samengevoegd met Bankflow; oude bookmarks blijven werken. */}
            <Route path="/bestandsbeheer" element={<Navigate to="/bankflow" replace />} />
            <Route path="/exact-online" element={page(<ExactOnlinePage />)} />
            <Route path="/exact-online/journals" element={page(<JournalsPage />)} />
            <Route path="/exact-online/general-ledger" element={page(<GeneralLedgerPage />)} />
            <Route path="/diagnostics" element={page(<Diagnostics />)} />
            <Route path="/lookups" element={page(<LookupsPage />)} />
            <Route path="/legal-entities" element={page(<LegalEntitiesPage />)} />
            <Route path="/feedback" element={page(<FeedbackPage />)} />
          </Route>

          {/* Catch-all: if not authenticated, go to login; else go home */}
          <Route
            path="*"
            element={isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <AuthInfoProvider>
      <ToastProvider>
        <AppInner />
        <SessionExpiredModal />
      </ToastProvider>
    </AuthInfoProvider>
  );
}

export default App;
